/**
 * MatchState の FE 側型 + 派生ヘルパ。 サーバ (`MatchState::to_broadcast_value` /
 * wasm `matchOpen` / `matchApplyMove`) の出力を読むだけで完結する。
 *
 * **重要**: 以前は FE 側に `completedTurnsOnBoard` / `nextBoardForSeat` を TS で再実装
 * していたが、 サーバ側 spec ([docs/ORCHESTRATOR.md] §2/§4) と乖離するたびにバグを生んだ
 * (= [[feedback_spec_first]])。 現在はサーバが `state._view` に派生値を載せて broadcast
 * するので、 FE はそれを読むだけ。 TS 再実装は復活させないこと。
 */
import type { GameId } from '../wasm';

export type Side = 'First' | 'Second';

/** マッチ全体の役。 マッチ開始時に固定 (`host_seat` でランダム配分)。 */
export type MatchSeat = 'Leader' | 'Follower';

export type Role = 'host' | 'guest' | 'spectator';

/** 1 手分のログ。 history に積まれる。 */
export interface MoveLogEntry {
  mv: unknown;
  side: Side;
  ply: number;
  at_ms: number;
}

export interface BoardEntry {
  board: number;
  game_id: GameId;
  state: unknown;
  current_side: Side | null;
  result: 'First' | 'Second' | 'Draw' | null;
  history: MoveLogEntry[];
  pending_draw_offer?: MatchSeat | null;
  /** この盤で `Side::First` を打つ MatchSeat。 マッチ開始時に固定。 */
  first_seat?: MatchSeat;
}

/** サーバが broadcast に乗せる派生ビュー (`MatchState::to_broadcast_value`)。 */
export interface SnapshotView {
  /** 各 seat が今この瞬間に指せる board (board_id 昇順、 空配列なら指せない)。 */
  allowed_boards: { Leader: number[]; Follower: number[] };
  /** 「今 clock が動いている side」 (= active_side)。 */
  active_side: MatchSeat;
  next_board_for_seat: { Leader: number | null; Follower: number | null };
  completed_turns_by_seat: { Leader: number; Follower: number };
  current_round_by_seat: { Leader: number; Follower: number };
}

export interface Snapshot {
  boards: BoardEntry[];
  /** Role::host が割り当てられた MatchSeat。 マッチを通じて固定。 */
  host_seat?: MatchSeat;
  /** サーバ派生ビュー。 「指せる board」 「動いている side」 はすべてここに載る。 */
  _view?: SnapshotView;
  outcome?: 'First' | 'Second' | 'Draw' | null;
}

/** Role + host_seat から自分の MatchSeat を求める。 観戦者なら null。 */
export function mySeatOf(snapshot: Snapshot | null, you: Role | null): MatchSeat | null {
  if (!you || you === 'spectator') return null;
  const hostSeat = snapshot?.host_seat ?? 'Leader';
  if (you === 'host') return hostSeat;
  return hostSeat === 'Leader' ? 'Follower' : 'Leader';
}

export function opponentOf(seat: MatchSeat): MatchSeat {
  return seat === 'Leader' ? 'Follower' : 'Leader';
}

/** board ごとの自分の per-board Side。 first_seat + Renju swap parity / Kalah pie 込み (= 物理色)。
 *  盤描画 (= 自分の駒を手前に / 自分の石色を反映) に使う。 勝敗判定には `slotSideOnBoard`。 */
export function sideOnBoard(
  snapshot: Snapshot | null,
  mySeat: MatchSeat | null,
  boardId: number,
): Side | null {
  if (!mySeat || !snapshot) return null;
  const entry = snapshot.boards.find((b) => b.board === boardId);
  if (!entry) return null;
  const fs: MatchSeat = entry.first_seat ?? 'Leader';
  let side: Side = mySeat === fs ? 'First' : 'Second';
  if (entry.game_id === 'Renju') {
    const sc = (entry.state as { swap_count?: number } | null)?.swap_count ?? 0;
    if (sc % 2 === 1) side = side === 'First' ? 'Second' : 'First';
  }
  if (entry.game_id === 'Kalah' && (entry.state as { swapped?: boolean } | null)?.swapped === true) {
    side = side === 'First' ? 'Second' : 'First';
  }
  return side;
}

/** board ごとの自分の **slot Side** (= first_seat ベース、 Renju Swap / Kalah pie でも反転しない)。
 *  `entry.result` (= renju_to_match で物理→slot 変換済み) との比較に使う。
 *  **注意**: 物理色を返す `sideOnBoard` と混同しないこと。 Swap 系で勝敗ラベルが
 *  逆転するバグの原因 (2026-06-15)。 */
export function slotSideOnBoard(
  snapshot: Snapshot | null,
  mySeat: MatchSeat | null,
  boardId: number,
): Side | null {
  if (!mySeat || !snapshot) return null;
  const entry = snapshot.boards.find((b) => b.board === boardId);
  if (!entry) return null;
  const fs: MatchSeat = entry.first_seat ?? 'Leader';
  return mySeat === fs ? 'First' : 'Second';
}

/** `mySeat` が当該 `boardId` を今この瞬間指せるか。 サーバ派生 `_view.allowed_boards` を直読。 */
export function myTurnAllowedOn(
  snapshot: Snapshot | null,
  mySeat: MatchSeat | null,
  boardId: number,
): boolean {
  if (!mySeat || !snapshot?._view) return false;
  return (snapshot._view.allowed_boards[mySeat] ?? []).includes(boardId);
}

/** 自分が指せる board が 1 つでもあるか。 */
export function hasAnyTurn(snapshot: Snapshot | null, mySeat: MatchSeat | null): boolean {
  if (!mySeat || !snapshot?._view) return false;
  return (snapshot._view.allowed_boards[mySeat] ?? []).length > 0;
}

/** 自分が今指せる board の集合 (board_id 昇順)。 */
export function allowedBoardsFor(
  snapshot: Snapshot | null,
  mySeat: MatchSeat | null,
): number[] {
  if (!mySeat || !snapshot?._view) return [];
  return snapshot._view.allowed_boards[mySeat] ?? [];
}

/** 今 clock が動いている side。 */
export function activeSideOf(snapshot: Snapshot | null): MatchSeat | null {
  return snapshot?._view?.active_side ?? null;
}

/** 自分が次に指す予定の board (サーバ派生ビューを直読)。 _view 不在なら null。 */
export function nextBoardForSeat(
  snapshot: Snapshot | null,
  seat: MatchSeat | null,
): number | null {
  if (!seat) return null;
  return snapshot?._view?.next_board_for_seat[seat] ?? null;
}

/** seat がマッチ全体で完了した turn 数 (サーバ派生ビューを直読)。 */
export function completedTurnsBySeat(
  snapshot: Snapshot | null,
  seat: MatchSeat | null,
): number {
  if (!seat) return 0;
  return snapshot?._view?.completed_turns_by_seat[seat] ?? 0;
}

/**
 * マッチ全体の MatchOutcome → Role 変換。
 *
 * **`MatchOutcome::First` は 「host の勝ち」 ではない。** マッチ全体の outcome は
 * MatchSeat 基準で、 `First` = `Leader` 席の勝ちを意味する。 どちらの席が host かは
 * `host_seat` で決まる (マッチ開始時に抽選)。 サーバはこの変換を
 * `mixed-do::format_outcome(o, host_seat)` で行ってから "host"/"guest"/"draw" を
 * broadcast するので、 **FE が生の MatchOutcome を訳すときも host_seat が要る**。
 * 落とすと host_seat=Follower のマッチで勝敗表示が丸ごと反転する (2026-09-19)。
 *
 * すでに Role 文字列 ("host"/"guest") になっているものはそのまま通す。
 */
export function matchOutcomeToRole(outcome: unknown, hostSeat: MatchSeat): 'host' | 'guest' | 'draw' {
  const s = String(outcome).toLowerCase();
  const hostIsLeader = hostSeat === 'Leader';
  if (s === 'first') return hostIsLeader ? 'host' : 'guest';
  if (s === 'second') return hostIsLeader ? 'guest' : 'host';
  if (s === 'host') return 'host';
  if (s === 'guest') return 'guest';
  return 'draw';
}

/** per-board Side → Role (board の first_seat + state.host_seat から)。
 *  既存 callsite の互換用; 新規コードは sideOnBoard / mySeatOf を経由するのが望ましい。 */
export function sideToRole(
  state: unknown,
  boardId: number,
  side: string,
): 'host' | 'guest' | null {
  const s = state as Snapshot | null;
  if (!s) return null;
  const entry = s.boards?.find((b) => b.board === boardId);
  if (!entry) return null;
  const fs: MatchSeat = entry.first_seat ?? 'Leader';
  const hostSeat: MatchSeat = s.host_seat ?? 'Leader';
  const isFirst = side === 'First' || side === 'Host';
  // Side::First を打つ MatchSeat = first_seat。 host_seat == first_seat なら host が First。
  const firstRole: 'host' | 'guest' = fs === hostSeat ? 'host' : 'guest';
  return isFirst ? firstRole : firstRole === 'host' ? 'guest' : 'host';
}
